import * as React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

type AvatarState = "loading" | "error" | "loaded";

interface IRootContext {
  status: AvatarState;
  setStatus: (status: AvatarState) => void;
}

const RootContext = React.createContext<IRootContext | null>(null);

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => {
  const [status, setStatus] = React.useState<AvatarState>("loading");
  return (
    <RootContext.Provider value={{ status, setStatus }}>
      <AvatarPrimitive.Root
        ref={ref}
        className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
        {...props}
      />
    </RootContext.Provider>
  );
});
Avatar.displayName = "Avatar";

function useRootContext() {
  const context = React.useContext(RootContext);
  if (!context) {
    throw new Error("Avatar compound components cannot be rendered outside the Avatar component");
  }
  return context;
}

interface AvatarImageProps extends Omit<
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>,
  "onLoadingStatusChange"
> {
  onLoadingStatusChange?: (status: "error" | "loaded") => void;
}

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ onLoadingStatusChange, className, ...props }, ref) => {
  const { setStatus, status } = useRootContext();

  const handleLoadingStatusChange = React.useCallback(
    (radixStatus: "idle" | "loading" | "loaded" | "error") => {
      if (radixStatus === "loaded") {
        setStatus("loaded");
        onLoadingStatusChange?.("loaded");
      } else if (radixStatus === "error") {
        setStatus("error");
        onLoadingStatusChange?.("error");
      }
    },
    [setStatus, onLoadingStatusChange],
  );

  if (status === "error") {
    return null;
  }

  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn("aspect-square h-full w-full object-cover", className)}
      onLoadingStatusChange={handleLoadingStatusChange}
      {...props}
    />
  );
});
AvatarImage.displayName = "AvatarImage";

const AvatarLoading = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { status } = useRootContext();

    if (status !== "loading") {
      return null;
    }

    return (
      <div ref={ref} className={cn("absolute inset-0", className)} {...props}>
        <Skeleton className="h-full w-full rounded-full" />
      </div>
    );
  },
);
AvatarLoading.displayName = "AvatarLoading";

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => {
  const { status } = useRootContext();

  if (status === "loading") {
    return null;
  }

  if (status === "loaded") {
    return null;
  }

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "bg-muted flex h-full w-full items-center justify-center rounded-full",
        className,
      )}
      {...props}
    />
  );
});
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarImage, AvatarFallback, AvatarLoading };
